import React, {
  Dispatch, ReactElement, SetStateAction, useState,
} from 'react';
import {
  View, StyleSheet, Text, ActivityIndicator,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useDispatch, useSelector } from 'react-redux';
import Colors from '../../../../constants/Colors';
import Style from '../../../../constants/Style';
import deleteFloor from '../../../../requests/Locations/Floors/DeleteFloor';
import { updateBuildingId } from '../../../../redux/selectedBuildingSlice';
import { REMOVE_FLOOR_FAILURE } from '../../../../constants/AlertMessages';
import { AppScreen } from '../../../../navigation/LinkingConfiguration';
import { UserPermission } from '../../../../constants/Types';
import { Area, Floor, Space } from '../../../../components/Map/SetFloorsModal/FloorItem';
import { logEvent } from '../../../../components/LogEvent';
import PressableStyled from '../../../../components/PressableStyled';
import FloorIcon from '../../../../components/16pxIcons/FloorIcon';
import PriorityIndicator from '../PriorityIndicator';
import TickIcon from '../../../../components/TickIcon';
import EmptyIcon from '../../../../components/24pxIcons/EmptyIcon';
import LocationIcon from '../../../../components/30pxIcons/LocationIcon';
import EditIcon24 from '../../../../components/24pxIcons/EditIcon24';
import DeleteIcon24 from '../../../../components/24pxIcons/DeleteIcon24';
import AlertModal from '../../../../components/AlertModal';
import SetFloorsModal from '../../../../components/Map/SetFloorsModal';

interface BuildingFloorItemProps {
  floor: Floor,
  parentName: string,
  setSelectedFloorId: Dispatch<SetStateAction<string>>,
  removeFloor,
  editFloor,
  floors: Floor[],
  testID?: string,
}

const BuildingFloorItem = ({
  floor, parentName, removeFloor, setSelectedFloorId, editFloor, floors, testID,
}: BuildingFloorItemProps) => {
  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const happyEnabled: boolean = useSelector((state: { happyEnabled: { enabled: boolean } }) => state
    .happyEnabled.enabled);
  const canUpdateLocations: boolean = isAdmin
    || permissions.includes(UserPermission.LOCATIONS_UPDATE);
  const canDeleteLocations: boolean = isAdmin
    || permissions.includes(UserPermission.LOCATIONS_DELETE);

  const [alertTitle, setAlertTitle] = useState<string>('');
  const [alertMessage, setAlertMessage] = useState<string | ReactElement>(null);
  const [alertError, setAlertError] = useState<boolean>(true);
  const [alertAction, setAlertAction] = useState<() => void>(null);
  const [alertActionTitle, setAlertActionTitle] = useState<string>(null);
  const [alertVisible, setAlertVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [editFloorModalVisible, setEditFloorModalVisible] = useState<boolean>(false);
  const navigation = useNavigation();
  const dispatch = useDispatch();

  const showAlert = (
    title: string,
    message: string | ReactElement,
    error: boolean = true,
    action: () => void = null,
    actionTitle: string = null,
  ) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertError(error);
    setAlertAction(() => action);
    setAlertActionTitle(actionTitle);
    setLoading(false);
    setAlertVisible(true);
  };

  const removeError = (errorText?: string) => showAlert('Error', errorText ?? REMOVE_FLOOR_FAILURE);

  const navigateToMap: () => void = () => {
    logEvent('LocationManagement', 'navigate_to_map');
    dispatch(updateBuildingId(floor.ParentId));
    const floorId: string = floor.LocationId;
    navigation.navigate(AppScreen.MAP as any, { floorId });
  };

  const edit: () => void = () => {
    logEvent('LocationManagement', 'edit_floor_pressed');
    setEditFloorModalVisible(true);
  };

  const remove: () => void = () => showAlert(
    'Delete floor?',
    <Text>
      {'Do you want to delete '}
      <Text style={styles.bold}>{floor.Name}</Text>
      {' from '}
      <Text style={styles.bold}>{parentName}</Text>
      {'?'}
    </Text>,
    true,
    () => {
      logEvent('LocationManagement', 'delete_floor');
      setLoading(true);
      deleteFloor(floor.LocationId).then((res) => {
        if (res && !res.isError) {
          setLoading(false);
          removeFloor(floor.LocationId, floor.Name);
        } else removeError(res?.errorText);
      }).catch((error) => removeError(error?.message));
    },
    'Delete',
  );

  let assetCount: number = floor.assetCount ?? 0;
  let sensorCount: number = floor.sensorCount ?? 0;
  let meterCount: number = floor.meterCount ?? 0;
  let areaCount: number = 0;
  Object.values(floor.areas ?? {}).forEach((area: Area) => {
    if (!area) return;
    areaCount += 1;
    assetCount += area.assetCount ?? 0;
    sensorCount += area.sensorCount ?? 0;
    meterCount += area.meterCount ?? 0;
    Object.values(area.spaces ?? {}).forEach((space: Space) => {
      if (!space) return;
      assetCount += space.assetCount ?? 0;
      sensorCount += space.sensorCount ?? 0;
      meterCount += space.meterCount ?? 0;
    });
  });

  return <>
    <PressableStyled
      style={[Style.row, styles.container]}
      onPress={() => setSelectedFloorId(floor.LocationId)}
      {...{ testID }}
    >
      <View style={[Style.row, styles.titleSection]}>
        <View style={styles.iconContainer}>
          <FloorIcon />
        </View>
        <View>
          <Text style={styles.title}>{floor.Name}</Text>
          <Text style={styles.number}>{`FLR: ${floor.Number ?? '-'}`}</Text>
        </View>
      </View>
      <View style={[Style.row, styles.prioritySection]}>
        <PriorityIndicator isHighPriority={floor.IsHighPriority} />
      </View>
      <View style={styles.planSection}>
        {(floor.Svg || floor.planFilename) ? <TickIcon /> : <EmptyIcon />}
      </View>
      <View style={styles.planSection}>
        <Text style={styles.title}>{areaCount}</Text>
      </View>
      <View style={styles.planSection}>
        <Text style={styles.title}>{assetCount}</Text>
      </View>
      {happyEnabled && <View style={styles.planSection}>
        <Text style={styles.title}>{sensorCount + meterCount}</Text>
      </View>}
      <View style={[Style.row, styles.actionSection]}>
        <PressableStyled
          onPress={navigateToMap}
          testID='lm-navigate-to-map-btn'
        >
          <LocationIcon color={Colors.light.hereworksBlue500} />
        </PressableStyled>
        {canUpdateLocations && <PressableStyled
          onPress={edit}
          testID='lm-edit-floor-btn'
        >
          <EditIcon24 style={styles.editIcon} />
        </PressableStyled>}
        {canDeleteLocations && <PressableStyled
          onPress={remove}
          testID='lm-delete-floor-btn'
        >
          <DeleteIcon24 />
        </PressableStyled>}
      </View>
      <AlertModal
        title={alertTitle}
        message={alertMessage}
        error={alertError}
        modalVisible={alertVisible}
        setModalVisible={setAlertVisible}
        action={alertAction}
        actionTitle={alertActionTitle}
      />
      <SetFloorsModal
        {...{ editFloor, floors }}
        deleteFloorToUpdate={() => removeFloor(floor.LocationId, floor.Name)}
        isVisible={editFloorModalVisible}
        hide={() => setEditFloorModalVisible(false)}
        floorToUpdate={floor}
        ParentId={floor.ParentId}
      />
    </PressableStyled>
    <ActivityIndicator
      animating={loading}
      size='large'
      style={Style.activityIndicator}
    />
  </>;
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: Colors.light.hereworksWhite100,
    borderRadius: 10,
    marginTop: 8,
    paddingHorizontal: 16,
    paddingVertical: 10,
  },
  titleSection: {
    flex: 0.36,
    justifyContent: 'flex-start',
  },
  prioritySection: {
    flex: 0.15,
  },
  planSection: { flex: 0.08 },
  actionSection: {
    flex: 0.18,
    textAlign: 'right',
    justifyContent: 'flex-end',
  },
  iconContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    width: 40,
    aspectRatio: 1,
    borderRadius: 20,
    backgroundColor: Colors.light.hereworksDarkBlue,
    marginEnd: 12,
  },
  title: {
    fontSize: 14,
    lineHeight: 21,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksBlack600,
  },
  number: {
    fontSize: 12,
    lineHeight: 15,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack400,
  },
  editIcon: {
    marginHorizontal: 12,
  },
  bold: { fontFamily: 'Roboto_700Bold' },
});

export default BuildingFloorItem;
